<script setup>
import { ref, nextTick } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import KnowledgeBaseAPI from 'dashboard/api/knowledgeBase';
import { useAlert } from 'dashboard/composables';

const emit = defineEmits(['saved']);

const dialogRef = ref(null);
const file = ref(null);
const fileInputRef = ref(null);
const isLoading = ref(false);
const isDragging = ref(false);

const open = () => {
  file.value = null;
  isLoading.value = false;
  isDragging.value = false;
  nextTick(() => {
    dialogRef.value?.open();
  });
};

const close = () => {
  dialogRef.value?.close();
};

const onFileChange = (e) => {
  const selected = e.target.files?.[0];
  if (selected) file.value = selected;
};

const onDrop = (e) => {
  isDragging.value = false;
  const dropped = e.dataTransfer.files?.[0];
  if (dropped) file.value = dropped;
};

const upload = async () => {
  if (!file.value) return;
  isLoading.value = true;
  try {
    const reader = new FileReader();
    const base64Promise = new Promise((resolve, reject) => {
      reader.onload = () => {
        const res = reader.result;
        const base64 = typeof res === 'string' ? res.split(',')[1] : '';
        resolve(base64);
      };
      reader.onerror = reject;
    });
    reader.readAsDataURL(file.value);
    const contentBase64 = await base64Promise;

    const res = await KnowledgeBaseAPI.uploadDocument({
      filename: file.value.name,
      content_base64: contentBase64,
    });

    const chunks = res.data?.chunks || 0;
    useAlert(`文档解析入库成功，已切分 ${chunks} 个知识片段 ✓`);
    emit('saved');
    close();
  } catch (error) {
    useAlert(error.response?.data?.detail || error.response?.data?.error || error.message || '文档上传解析失败');
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    title="上传企业文档"
    confirm-button-label="解析并入库"
    cancel-button-label="取消"
    :is-loading="isLoading"
    :disable-confirm-button="!file"
    width="lg"
    @confirm="upload"
  >
    <div class="flex flex-col gap-4 py-2">
      <div
        class="flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-xl transition-colors cursor-pointer text-center"
        :class="isDragging ? 'border-n-brand bg-n-brand/10' : 'border-n-weak hover:border-n-slate-8 bg-n-alpha-1'"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="onDrop"
        @click="fileInputRef?.click()"
      >
        <input
          ref="fileInputRef"
          type="file"
          class="hidden"
          accept=".txt,.md,.markdown,.pdf,.docx"
          @change="onFileChange"
        />

        <div class="p-3 mb-2 rounded-full bg-n-alpha-2 text-n-slate-11">
          <svg class="size-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </div>

        <span v-if="!file" class="text-sm font-medium text-n-slate-12">
          点击选择文件，或将文件拖拽至此处
        </span>
        <span v-else class="text-sm font-semibold text-n-brand truncate max-w-xs">
          已选文件：{{ file.name }} ({{ Math.round(file.size / 1024) }} KB)
        </span>

        <p class="mt-1 text-xs text-n-slate-10">
          支持 TXT、Markdown、PDF、DOCX 等格式，自动分块向量化
        </p>
      </div>
    </div>
  </Dialog>
</template>
